import Button from '@/components/Button';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import { animationConfig, getPageAnimation } from '@/config/animations';
import { motion } from 'framer-motion';
import { Box, Check, Cloud, Code, Database, Layout, Mail, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const PricingPage = () => {
  const AnimationWrapper = animationConfig.enablePageTransitions ? motion.div : 'div';
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const apiUrl = import.meta.env.VITE_API_URL;

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${apiUrl}api/waitlist/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        setEmail('');
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(data.message || 'Failed to join waitlist. Please try again.');
      }
    } catch (err) {
      setError('Failed to join waitlist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    {
      icon: Layout,
      title: 'Frontend Development',
      description: 'Modern, responsive web applications built with the latest frameworks.',
      features: [
        'React & TypeScript development',
        'Responsive UI/UX implementation',
        'Component library creation',
        'Performance optimization',
      ],
    },
    {
      icon: Code,
      title: 'Backend Development',
      description: 'Scalable APIs and services for reliable performance.',
      features: [
        'RESTful API development',
        'Node.js & .NET solutions',
        'Serverless architecture',
        'Authentication & security',
      ],
    },
    {
      icon: Cloud,
      title: 'AWS Cloud Solutions',
      description: 'Cost-effective cloud infrastructure with serverless architecture.',
      features: [
        'Lambda & API Gateway',
        'CloudFront & S3 deployment',
        'Serverless architecture design',
        'Auto-scaling solutions',
      ],
    },
    {
      icon: Database,
      title: 'Database Solutions',
      description: 'Optimized data modeling and query performance.',
      features: [
        'PostgreSQL design & optimization',
        'DynamoDB implementation',
        'Data migration services',
        'Query performance tuning',
      ],
    },
    {
      icon: Box,
      title: 'DevOps & Infrastructure',
      description: 'Infrastructure as Code and automated deployment pipelines.',
      features: [
        'IaC with SST & Terraform',
        'Docker containerization',
        'CI/CD pipeline setup',
        'Monitoring & logging',
      ],
    },
    {
      icon: Sparkles,
      title: 'AI Integration',
      description: 'Integrate AWS AI services into your applications.',
      features: [
        'AWS Bedrock integration',
        'Agent Core implementation',
        'Custom prompt engineering',
        'AI workflow automation',
      ],
    },
  ];

  return (
    <AnimationWrapper
      {...getPageAnimation()}
      className="min-h-screen bg-gradient-to-b from-dark-950 via-dark-900 to-dark-950"
    >
      <Navbar />

      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Development <span className="text-gradient">Services</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Full-stack development services from frontend to cloud infrastructure.
              Custom solutions built with modern technologies to meet your specific needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-effect p-8 rounded-2xl"
              >
                <div className="inline-flex p-3 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 mb-6">
                  <service.icon className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400 mb-6">
                  {service.description}
                </p>

                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="glass-effect rounded-2xl p-12 mb-16"
          >
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-4">
                How It Works
              </h2>
              <p className="text-gray-400 mb-8">
                We work with you to understand your needs and deliver custom solutions.
                Every project is unique, and pricing is based on scope and requirements.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary-500 text-white font-bold text-xl flex items-center justify-center mx-auto mb-3">
                    1
                  </div>
                  <h3 className="text-white font-semibold mb-2">Discovery Call</h3>
                  <p className="text-gray-400 text-sm">Discuss your needs and objectives</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary-500 text-white font-bold text-xl flex items-center justify-center mx-auto mb-3">
                    2
                  </div>
                  <h3 className="text-white font-semibold mb-2">Proposal</h3>
                  <p className="text-gray-400 text-sm">Receive detailed scope and pricing</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary-500 text-white font-bold text-xl flex items-center justify-center mx-auto mb-3">
                    3
                  </div>
                  <h3 className="text-white font-semibold mb-2">Delivery</h3>
                  <p className="text-gray-400 text-sm">Iterative development and launch</p>
                </div>
              </div>

              <Link to="/contact">
                <Button size="lg">
                  Schedule Consultation
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center glass-effect rounded-3xl p-12"
          >
            <Mail className="w-12 h-12 text-primary-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Interested in VisualForge?
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Join the waitlist for early access to VisualForge, our AI-powered creative platform.
            </p>
            <form onSubmit={handleWaitlistSubmit} className="max-w-md mx-auto">
              <div className="flex gap-4">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={submitting}
                  className="flex-1 px-6 py-4 rounded-lg bg-dark-800 border border-dark-700 text-white focus:border-primary-500 focus:outline-none transition-colors disabled:opacity-50"
                />
                <Button type="submit" size="lg" disabled={submitting}>
                  {submitting ? 'Joining...' : success ? 'Joined!' : 'Join Waitlist'}
                </Button>
              </div>
              {error && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
                  Welcome to the waitlist! Check your email for confirmation.
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </AnimationWrapper>
  );
};

export default PricingPage;